/**
 * Auth Service - 认证服务
 * 提供用户注册、登录、登出等功能
 */
import api from './api'

// ============================================
// 类型定义
// ============================================

export interface User {
  id: string
  username: string
  email: string
  tenantId: string
  isActive: boolean
  createdAt: string
}

export interface AuthResponse {
  accessToken: string
  tokenType: string
  expiresIn: number
  user: User
}

export interface RegisterRequest {
  username: string
  email: string
  password: string
}

export interface LoginRequest {
  email: string
  password: string
}

export interface AvailabilityResponse {
  available: boolean
  message?: string
}

// ============================================
// Token 管理
// ============================================

const TOKEN_KEY = 'token'
const USER_KEY = 'user'

export const TokenStorage = {
  get(): string | null {
    return localStorage.getItem(TOKEN_KEY)
  },
  
  set(token: string): void {
    localStorage.setItem(TOKEN_KEY, token)
  },
  
  remove(): void {
    localStorage.removeItem(TOKEN_KEY)
  },
  
  getUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY)
    if (userStr) {
      try {
        return JSON.parse(userStr)
      } catch {
        return null
      }
    }
    return null
  },
  
  setUser(user: User): void {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  },
  
  removeUser(): void {
    localStorage.removeItem(USER_KEY)
  },
  
  clear(): void {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }
}

// ============================================
// API 调用
// ============================================

/**
 * 用户注册
 */
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/register', data)
  
  // 保存 token 和用户信息
  TokenStorage.set(response.data.accessToken)
  TokenStorage.setUser(response.data.user)
  
  return response.data
}

/**
 * 用户登录
 */
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await api.post<AuthResponse>('/auth/login', data)
  
  // 保存 token 和用户信息
  TokenStorage.set(response.data.accessToken)
  TokenStorage.setUser(response.data.user)
  
  return response.data
}

/**
 * 用户登出
 */
export function logout(): void {
  TokenStorage.clear()
  window.location.href = '/login'
}

/**
 * 获取当前用户信息
 */
export async function getCurrentUser(): Promise<User> {
  const response = await api.get<User>('/auth/me')
  
  // 更新本地存储
  TokenStorage.setUser(response.data)
  
  return response.data
}

/**
 * 检查用户名可用性
 */
export async function checkUsernameAvailable(username: string): Promise<AvailabilityResponse> {
  const response = await api.post<AvailabilityResponse>('/auth/check-username', { username })
  return response.data
}

/**
 * 检查邮箱可用性
 */
export async function checkEmailAvailable(email: string): Promise<AvailabilityResponse> {
  const response = await api.post<AvailabilityResponse>('/auth/check-email', { email })
  return response.data
}

/**
 * 检查是否已登录
 */
export function isAuthenticated(): boolean {
  return !!TokenStorage.get()
}

/**
 * 获取本地存储的用户信息
 */
export function getStoredUser(): User | null {
  return TokenStorage.getUser()
}

// 默认导出
export default {
  register,
  login,
  logout,
  getCurrentUser,
  checkUsernameAvailable,
  checkEmailAvailable,
  isAuthenticated,
  getStoredUser,
  TokenStorage
}

